<section class="tlrl" data-testid="runner-live" [attr.aria-label]="'testRunner.live.aria' | translate">
  @if (connectionLost()) {
    <app-live-reconnect-hint class="tlrl-reconnect" messageKey="testRunner.live.reconnecting" />
  }

  <ng-template #summary>
    <p class="tlrl-summary" data-testid="live-pipeline-summary">
      @if (summaryShowsPill()) {
        <ng-container [ngTemplateOutlet]="pill" />
      }
      @if (reportSummary(); as parts) {
        @for (part of parts; track part.key; let first = $first) {
          @if (!first) {
            <span class="tlrl-summary__sep" aria-hidden="true">·</span>
          }
          @if (part.strong) {
            <strong class="tlrl-summary__failed">{{ part.key | translate: part.params }}</strong>
          } @else {
            <span>{{ part.key | translate: part.params }}</span>
          }
        }
        @if (elapsed(); as clock) {
          <span class="tlrl-summary__sep" aria-hidden="true">·</span>
          <span>{{ 'testRunner.live.summary.since' | translate: { elapsed: clock } }}</span>
        }
      } @else {
        <span>{{ summaryKey() | translate: { done: rollup().done, total: rollup().total } }}</span>
        @if (failures().length > 0) {
          <span class="tlrl-summary__sep" aria-hidden="true">·</span>
          <strong class="tlrl-summary__failed">
            {{ failureCountKey() | translate: { count: failures().length } }}
          </strong>
        }
        <span class="tlrl-summary__sep" aria-hidden="true">·</span>
        @if (elapsed(); as clock) {
          <span>{{ 'testRunner.live.summary.since' | translate: { elapsed: clock } }}</span>
        } @else {
          <span>{{ 'testRunner.live.summary.finished' | translate }}</span>
        }
      }
      @if (!pipeline() && showsCiReport() && unmatchedTests() > 0) {
        <span class="tlrl-summary__sep" aria-hidden="true">·</span>
        <ng-container [ngTemplateOutlet]="unmatchedHint" />
      }
    </p>
  </ng-template>

  <ng-template #pill>
    <app-res-pill
      data-testid="live-pipeline-status"
      [status]="pipelinePill().status"
      [label]="pipelinePill().label | translate"
      [pulsing]="pipelinePill().pulsing"
    />
  </ng-template>

  <ng-template #unmatchedHint>
    <button type="button" class="tlrl-pipeline__hint" data-testid="unmatched-tests-hint" (click)="showUnmatchedTests()">
      {{ unmatchedTestsKey() | translate: { count: unmatchedTests() } }}
      <span aria-hidden="true">›</span>
    </button>
  </ng-template>

  @if (ursacheHinweis()) {
    <p class="tlrl-ki-hinweis" role="status" aria-live="polite" data-testid="ki-pipeline-hinweis">
      <app-ki-mark [size]="16" />
      <span>{{ 'ki.pipeline.hinweis' | translate }}</span>
    </p>
  } @else if (ursache(); as befund) {
    <app-ki-pipeline-ursache
      [befund]="befund"
      [status]="befund.status"
      [ciUrl]="ursacheCiUrl()"
      [projectId]="projectId()"
      [ausfuehrungId]="executionId()"
      [kiAnfrageId]="kiAnfrageId()"
      [aktionAnbieten]="kiAktionAnbieten()"
      [verknuepfterBug]="durchlaufBugLink()"
      [erneutStartenAnbieten]="canRerun()"
      (aktionVorlage)="openKiBugDialog($event)"
      (erneutStarten)="rerun()"
    />
  }

  @if (pipeline(); as card) {
    <section
      class="tlrl-pipeline"
      [class.tlrl-pipeline--live]="live()"
      data-testid="live-pipeline-card"
      aria-labelledby="tlrl-pipeline-title"
    >
      <span
        class="tlrl-pipeline__provider"
        [class]="'tlrl-pipeline__provider--' + card.provider.tone"
        data-testid="live-pipeline-provider"
        role="img"
        [attr.aria-label]="card.provider.label | translate"
      >
        @if (card.provider.brandIcon; as brand) {
          <i [class]="brand" aria-hidden="true"></i>
        } @else {
          <mat-icon aria-hidden="true">account_tree</mat-icon>
        }
      </span>
      <div class="tlrl-pipeline__body">
        <p class="tlrl-pipeline__title" id="tlrl-pipeline-title">{{ 'testRunner.live.pipeline.title' | translate }}</p>
        <div class="tlrl-pipeline__line">
          <a
            class="tlrl-pipeline__link"
            data-testid="live-pipeline-link"
            [href]="card.runUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span data-testid="live-pipeline-name">{{ card.label }}</span>
            <mat-icon aria-hidden="true">open_in_new</mat-icon>
          </a>
          <ng-container [ngTemplateOutlet]="pill" />
          @if (showsCiReport() && unmatchedTests() > 0) {
            <ng-container [ngTemplateOutlet]="unmatchedHint" />
          }
        </div>
        <ng-container [ngTemplateOutlet]="summary" />
      </div>
      <div class="tlrl-pipeline__actions">
        @if (canCancel()) {
          <button
            mat-stroked-button
            type="button"
            data-testid="pipeline-cancel"
            [disabled]="actionBusy()"
            (click)="askCancel()"
          >
            <mat-icon aria-hidden="true">stop_circle</mat-icon>
            {{ 'testRunner.live.pipeline.cancel' | translate }}
          </button>
        }
        @if (canRerun()) {
          <button
            mat-stroked-button
            type="button"
            data-testid="pipeline-rerun"
            [disabled]="actionBusy()"
            (click)="rerun()"
          >
            <mat-icon aria-hidden="true">refresh</mat-icon>
            {{ 'testRunner.live.pipeline.rerun' | translate }}
          </button>
        }
        @if (card.logUrl; as logUrl) {
          <a mat-stroked-button data-testid="pipeline-logs" [href]="logUrl" target="_blank" rel="noopener noreferrer">
            <mat-icon aria-hidden="true">open_in_new</mat-icon>
            {{ 'testRunner.live.pipeline.logs' | translate }}
          </a>
        }
      </div>
      @if (actionError(); as failure) {
        <p class="tlrl-pipeline__error" role="alert" data-testid="pipeline-action-error">
          <mat-icon aria-hidden="true">error_outline</mat-icon>
          <span>{{ failure.key | translate }}</span>
          @if (failure.showsAccountLink) {
            <a data-testid="pipeline-action-error-account" routerLink="/account">
              {{ 'testRunner.live.pipeline.error.toAccount' | translate }}
            </a>
          }
        </p>
      }
    </section>
  }

  @if (state().runnerJob; as job) {
    <app-runner-job-card
      [projectId]="projectId()"
      [runnerJob]="job"
      [hasStepResults]="hasStepResults()"
      [canWrite]="canAct()"
      (changed)="staleState.emit()"
    />
  }

  <app-confirm-dialog
    icon="stop_circle"
    [visible]="cancelConfirmVisible()"
    [busy]="actionBusy()"
    [title]="'testRunner.live.pipeline.cancelConfirm.title' | translate"
    [message]="'testRunner.live.pipeline.cancelConfirm.message' | translate"
    [confirmLabel]="'testRunner.live.pipeline.cancelConfirm.confirm' | translate"
    [cancelLabel]="'testRunner.live.pipeline.cancelConfirm.cancel' | translate"
    (confirmed)="confirmCancel()"
    (cancelled)="dismissCancel()"
  />

  <div class="tlrl-rollup" data-testid="live-rollup">
    @if (!pipeline()) {
      <ng-container [ngTemplateOutlet]="summary" />
    }

    @if (hasPipelineSteps()) {
      <app-pipe-track
        class="tlrl-rollup__track"
        data-testid="live-pipe-track"
        size="md"
        [cells]="trackCells()"
        [label]="trackSummary()"
      />
    }

    <app-progress-bar
      variant="pill"
      [total]="rollup().total"
      [passed]="rollup().passed"
      [failed]="rollup().failed"
      [skipped]="rollup().skipped"
      [blocked]="rollup().blocked"
      [notRun]="rollup().notRun"
    />
    <ul class="tlrl-rollup__counts">
      <li data-testid="live-rollup-passed">
        <strong class="tlrl-rollup__value tlrl-rollup__value--passed">{{ rollup().passed }}</strong>
        {{ 'testRunner.live.passed' | translate }}
      </li>
      <li data-testid="live-rollup-failed">
        <strong class="tlrl-rollup__value tlrl-rollup__value--failed">{{ rollup().failed }}</strong>
        {{ 'testRunner.live.failed' | translate }}
      </li>
      <li data-testid="live-rollup-skipped">
        <strong class="tlrl-rollup__value">{{ rollup().skipped }}</strong>
        {{ 'testRunner.live.skipped' | translate }}
      </li>
      @if (rollup().blocked > 0) {
        <li data-testid="live-rollup-blocked">
          <strong class="tlrl-rollup__value">{{ rollup().blocked }}</strong>
          {{ 'testRunner.live.blocked' | translate }}
        </li>
      }
      @if (rollup().notRun > 0) {
        <li data-testid="live-rollup-not-run">
          <strong class="tlrl-rollup__value">{{ rollup().notRun }}</strong>
          {{ 'testRunner.live.notRun' | translate }}
        </li>
      }
      <li data-testid="live-rollup-open">
        <strong class="tlrl-rollup__value">{{ rollup().open }}</strong>
        {{ 'testRunner.live.stillOpen' | translate }}
      </li>
    </ul>
  </div>

  @if (hasPipelineSteps()) {
    <div
      #stepsBox
      class="tlrl-steps"
      role="region"
      data-testid="pipeline-step-list"
      [attr.tabindex]="stepsTabIndex()"
      [attr.aria-label]="'testRunner.live.pipelineSteps.aria' | translate"
    >
      <table class="tlrl-steps__table">
        <caption class="sr-only">
          {{ 'testRunner.live.pipelineSteps.aria' | translate }}
        </caption>
        <thead>
          <tr>
            <th class="tlrl-steps__col tlrl-steps__col--nr" scope="col">
              {{ 'testRunner.live.pipelineSteps.column.nr' | translate }}
            </th>
            <th class="tlrl-steps__col tlrl-steps__col--name" scope="col">
              {{ 'testRunner.live.pipelineSteps.column.step' | translate }}
            </th>
            <th class="tlrl-steps__col tlrl-steps__col--result" scope="col">
              {{ 'testRunner.live.pipelineSteps.column.result' | translate }}
            </th>
            <th class="tlrl-steps__col tlrl-steps__col--duration" scope="col">
              {{ 'testRunner.live.pipelineSteps.column.duration' | translate }}
            </th>
          </tr>
        </thead>
        <tbody>
          @for (step of pipelineSteps(); track step.id) {
            <tr class="tlrl-steps__row" data-testid="pipeline-step-row">
              <th class="tlrl-steps__col tlrl-steps__col--nr" scope="row">{{ step.nr }}</th>
              <td class="tlrl-steps__col tlrl-steps__col--name">{{ step.name }}</td>
              <td class="tlrl-steps__col tlrl-steps__col--result">
                <app-res-pill [status]="pillStatus(step.status)" [label]="resultKey(step.status) | translate" />
              </td>
              <td class="tlrl-steps__col tlrl-steps__col--duration">{{ stepDuration(step) }}</td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  }

  @if (showsCaseList()) {
    <app-runner-case-list [cases]="cases()" />
  }

  @if (failures().length > 0) {
    <section
      class="tlrl-failures"
      data-testid="live-failure-card"
      [attr.aria-label]="'testRunner.live.failures.aria' | translate"
    >
      <p class="tlrl-failures__head" data-testid="live-failure-title">
        <mat-icon aria-hidden="true">close</mat-icon>
        <strong class="tlrl-failures__title">{{ failureTitleKey() | translate: { count: failures().length } }}</strong>
        <span class="tlrl-failures__hint">{{ 'testRunner.live.failures.hint' | translate }}</span>
      </p>
      <ul class="tlrl-failures__list">
        @for (failure of failures(); track failure.caseResultId) {
          <li class="tlrl-failures__item" data-testid="live-failure-entry">
            <code class="tlrl-failures__id">{{ failure.autoId }}</code>
            <span class="tlrl-failures__name">{{ failure.name }}</span>
            <span class="tlrl-failures__when" data-testid="live-failure-when">
              {{ failure.at ? (failure.at | date: 'HH:mm:ss') : '—' }}
            </span>
            @if (failure.linkedBugKey; as bugKey) {
              <a
                class="tlrl-failures__bug"
                data-testid="live-failure-bug-link"
                [attr.data-case-result]="failure.caseResultId"
                target="_blank"
                rel="noopener noreferrer"
                [href]="bugUrl(failure)"
                [attr.aria-label]="
                  'testRunner.live.failures.bugOpenAria' | translate: { key: bugKey, test: failure.name }
                "
              >
                {{ bugKey }}
                <mat-icon aria-hidden="true">open_in_new</mat-icon>
              </a>
            } @else if (canCreateBug()) {
              <button
                type="button"
                class="tlrl-failures__bug"
                data-testid="live-failure-bug-action"
                aria-haspopup="dialog"
                [attr.aria-label]="'testRunner.live.failures.bugCreateAria' | translate: { test: failure.name }"
                (click)="openBugDialog(failure)"
              >
                <mat-icon aria-hidden="true">add</mat-icon>
                {{ 'testRunner.bug.create' | translate }}
                <mat-icon aria-hidden="true">open_in_new</mat-icon>
              </button>
            }
          </li>
        }
      </ul>
    </section>
  }

  @if (bugDialogContext(); as context) {
    <app-live-bug-dialog
      [context]="context"
      [targets]="bugTargets()"
      [submitting]="bugSubmitting()"
      [error]="bugError()"
      [existingBugKey]="bugDialogExistingKey()"
      [existingBugUrl]="bugDialogExistingUrl()"
      [preview]="bugPreview()"
      [(choice)]="bugTargetChoice"
      (confirmed)="confirmBug($event)"
      (cancelled)="closeBugDialog()"
    />
  }

  @if (showsCiReport()) {
    <div class="tlrl-rule">
      <button
        type="button"
        class="tlrl-rule__toggle"
        data-testid="matching-rule-toggle"
        [attr.aria-expanded]="matchingRuleOpen()"
        [attr.aria-controls]="matchingRuleId"
        (click)="toggleMatchingRule()"
      >
        <mat-icon aria-hidden="true">info_outline</mat-icon>
        {{ 'testRunner.live.matchingRule.toggle' | translate }}
        <mat-icon aria-hidden="true">{{ matchingRuleOpen() ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>
      <p class="tlrl-rule__panel" data-testid="matching-rule" [id]="matchingRuleId" [hidden]="!matchingRuleOpen()">
        <strong>{{ 'ciMapping.ruleTitle' | translate }}</strong>
        {{ 'ciMapping.ruleLead' | translate }}
        <code>[TC-…]</code>
        {{ 'ciMapping.ruleRest' | translate }}
      </p>
    </div>

    <app-report-test-table
      class="tlrl-report"
      tabindex="-1"
      [projectId]="projectId()"
      [runId]="runId()"
      [executionId]="executionId()"
      [completed]="!live()"
      [canWrite]="canAct()"
      [bugRun]="bugRun()"
      (assignmentChanged)="loadCounts()"
      (bugCreated)="markBugLinked($event.caseResultId, $event.key)"
    />
  }
</section>
